/**
 * @fileoverview Input/output schemas for the run-refine-v2 composite agent.
 *
 * This composite agent orchestrates a two-step pipeline:
 * 1. refine-synthesizer: Creates an editorial brief from selected findings/weaknesses
 * 2. draft-refiner-v2: Refines the chapter based on the editorial brief
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  IssueSeverityEnum,
  IssueConfidenceEnum,
  IssueDomainEnum,
} from '@/ai/agents/issue-classification';

// =============================================================================
// SHARED SCHEMAS (used by hook and flow)
// =============================================================================

/**
 * Schema for a finding from critique agents.
 * Uses the unified issue classification schema.
 */
export const FindingSchema = z.object({
  id: z.string().describe('Unique identifier for the finding.'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  category: z.string().describe('Category of the finding from unified taxonomy.'),
  description: z.string().describe('Description of the issue.'),
  excerpt: z.string().describe('The text excerpt where the issue was found.'),
  suggestion: z.string().optional().describe('Suggested fix.'),
});

/**
 * Schema for a weakness from critique agents.
 * Uses the unified issue classification schema.
 */
export const WeaknessSchema = z.object({
  id: z.string().describe('Unique identifier for the weakness.'),
  severity: IssueSeverityEnum.describe('Severity level: must_fix, should_fix, or could_fix'),
  confidence: IssueConfidenceEnum.describe('Confidence level: certain, likely, or possible'),
  domain: IssueDomainEnum.describe('The domain of this weakness'),
  description: z.string().describe('Description of the weakness.'),
});

/**
 * Schema for a strength from critique agents.
 */
export const StrengthSchema = z.object({
  id: z.string().describe('Unique identifier for the strength.'),
  text: z.string().describe('Description of the strength.'),
});

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Minimal input from UI/hooks - what the user/UI provides.
 */
export const HookInputSchema = z.object({
  chapterId: z.string().describe('The ID of the chapter being refined'),
  chapterContent: z.string().describe('The current chapter HTML content from the editor'),
  findings: z.array(FindingSchema).describe('Selected findings to address'),
  weaknesses: z.array(WeaknessSchema).describe('Selected weaknesses to address'),
  strengths: z.array(StrengthSchema).describe('All strengths to preserve'),
  additionalNotes: z.string().optional().describe('Optional user instructions'),
  refinerModel: z.string().optional().describe(
    'Optional model override (e.g. "openai/gpt-5.2") applied only to the ' +
    'draft-refiner-v2 step. Used when the caller routes a refine session ' +
    'through the AI control panel and the user explicitly picks a model.'
  ),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context.
 *
 * Combines fields needed by both pipeline steps:
 * - refine-synthesizer: findings/weaknesses/strengths/additionalNotes/pov/chapterPovCharacter
 * - draft-refiner-v2: chapterContent + story-aware context (chapterContext,
 *   nextChapterContext, chapterPovCharacter, latestKnownPlotState, charactersCurrent,
 *   storyContext for genre)
 *
 * Style inputs (styleGuide, writingStyleSample) are intentionally NOT included
 * — the refiner is style-passive and matches the existing style of the draft.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  // Refiner: the draft to refine
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  // Refiner: character voice (also used by synthesizer indirectly)
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  // Refiner: chapter structural context
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  // Refiner: continuity context
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  // Refiner: genre awareness
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  // Synthesizer: POV (separate from chapterPovCharacter)
  ...AGENT_SCHEMA_OBJECTS.pov.schema.shape,
  // Refine session data
  findings: z.array(FindingSchema).describe('Selected findings to address'),
  weaknesses: z.array(WeaknessSchema).describe('Selected weaknesses to address'),
  strengths: z.array(StrengthSchema).describe('All strengths to preserve'),
  additionalNotes: z.string().optional().describe('Optional user instructions'),
  refinerModel: z.string().optional().describe(
    'Optional model override (e.g. "openai/gpt-5.2") applied only to the ' +
    'draft-refiner-v2 step.'
  ),
}).partial({
  nextChapterContext: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output includes the refined content plus detailed step information.
 */
export const OutputSchema = z.object({
  content: z.string().describe(
    'The refined chapter content, formatted as a single HTML string.'
  ),
  commentary: z.string().describe(
    'Combined commentary explaining the synthesis approach and refinement changes. ' +
    'Includes analysis of how strengths were preserved.'
  ),
  steps: z.object({
    synthesize: z.object({
      editorialBrief: z.string().describe('The editorial brief generated for the refiner'),
      prioritizedIssues: z.array(z.string()).describe('Issues in priority order'),
      strengthsToPreserve: z.array(z.string()).describe('Strengths that were protected'),
      synthesisRationale: z.string().describe('Why issues were prioritized this way'),
      durationMs: z.number().describe('Time taken for synthesis step'),
    }),
    refine: z.object({
      commentary: z.string().describe('Refiner agent commentary'),
      durationMs: z.number().describe('Time taken for refinement step'),
      model: z.string().describe('The model used for the refinement step'),
    }),
  }).optional().describe('Detailed results from each pipeline step'),
});

export type Output = z.infer<typeof OutputSchema>;
